import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface ActionMenuItem {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  className?: string;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ items, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, openUpward: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Calculate position when opening
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = 200; // Estimated max menu height
    const menuWidth = 192; // w-48 = 12rem = 192px
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;

    // Calculate left position, ensuring menu stays within viewport
    let left = rect.right - menuWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > viewportWidth - 8) {
      left = viewportWidth - menuWidth - 8;
    }

    setPosition({
      top: openUpward ? rect.top - 8 : rect.bottom + 8,
      left,
      openUpward,
    });
  }, []);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen) {
      updatePosition();
      setFocusedIndex(-1);
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => {
            const next = prev + 1;
            return next >= items.length ? 0 : next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => {
            const next = prev - 1;
            return next < 0 ? items.length - 1 : next;
          });
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < items.length) {
            const item = items[focusedIndex];
            if (item && !item.disabled) {
              item.onClick();
              setIsOpen(false);
            }
          }
          break;
        case 'Tab':
          setIsOpen(false);
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, focusedIndex, items]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  const handleItemClick = (item: ActionMenuItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.disabled) return;
    item.onClick();
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={cn(
          'inline-flex items-center justify-center p-1.5 rounded-md',
          'text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-muted)]',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-1',
          className
        )}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-orientation="vertical"
          className={cn(
            'fixed z-[9999] w-48',
            // Glass morphism background
            'bg-[var(--bg-elevated)]/95 backdrop-blur-xl',
            'border border-[var(--border-primary)]/60',
            'rounded-lg',
            // Multi-layered shadows for depth
            'shadow-[0_4px_6px_rgba(0,0,0,0.1),0_10px_20px_rgba(0,0,0,0.15),0_0_0_1px_rgba(255,255,255,0.05)_inset]',
            // Animation classes
            'animate-action-menu-in',
            position.openUpward ? 'origin-bottom-right' : 'origin-top-right'
          )}
          style={{
            top: position.openUpward ? 'auto' : position.top,
            bottom: position.openUpward ? window.innerHeight - position.top : 'auto',
            left: position.left,
          }}
        >
          <div className="py-1.5">
            {items.map((item, index) => (
              <button
                key={index}
                role="menuitem"
                tabIndex={-1}
                onClick={(e) => handleItemClick(item, e)}
                disabled={item.disabled}
                className={cn(
                  'flex items-center gap-3 w-full px-3 py-2 text-sm text-left',
                  'transition-all duration-150',
                  item.variant === 'danger'
                    ? 'text-status-error hover:bg-status-error/10'
                    : 'text-[var(--text)] hover:bg-[var(--bg-hover)]',
                  item.disabled && 'opacity-50 cursor-not-allowed',
                  focusedIndex === index && 'bg-[var(--bg-hover)]',
                  'focus:outline-none'
                )}
              >
                {item.icon && (
                  <item.icon className={cn(
                    'w-4 h-4 flex-shrink-0',
                    item.variant === 'danger' ? 'text-status-error' : 'text-[var(--muted)]'
                  )} />
                )}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ActionMenu;
export { ActionMenu };
